import React, { useState, useEffect } from "react"

// ─── CONFIG ────────────────────────────────────────────────────────────────
const TIMER_TOTAL_SECONDS = 600   // 10 minutes — the ring is full at this point
const RED_BLINK_AT_SECONDS = 40   // turn red + pulse when this many seconds remain
// ───────────────────────────────────────────────────────────────────────────

const RING_SIZE = 24
const RING_STROKE = 3.5
const RING_R = (RING_SIZE - RING_STROKE) / 2
const RING_CIRC = 2 * Math.PI * RING_R

const pad = n => String(n).padStart(2, "0")

const formatTime = total => {
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`
}

// Compact pill timer: a small progress ring + mm:ss, sized to sit in line
// with the question tabs. Shows `duration` (seconds) when the parent drives
// it, so it matches the time that gets submitted; otherwise counts up on its
// own from mount. Styles live in index.css (.ct-pill).
const CircularTimer = ({ duration, className = "" }) => {
  const isControlled = typeof duration === "number"
  const [ownElapsed, setOwnElapsed] = useState(0)

  useEffect(() => {
    if (isControlled) return
    const id = setInterval(() => setOwnElapsed(prev => prev + 1), 1000)
    return () => clearInterval(id)
  }, [isControlled])

  const elapsed = Math.max(0, isControlled ? duration : ownElapsed)
  const progress = Math.min(elapsed / TIMER_TOTAL_SECONDS, 1)
  const timeRemaining = TIMER_TOTAL_SECONDS - elapsed
  const isWarning = timeRemaining > 0 && timeRemaining <= RED_BLINK_AT_SECONDS

  const mins = Math.floor(elapsed / 60)
  const secs = elapsed % 60
  const c = RING_SIZE / 2

  return (
    <div
      className={`ct-pill${isWarning ? " ct-pill--warning" : ""} ${className}`}
      role="timer"
      aria-label={`Time on this question: ${mins} minutes ${secs} seconds`}
      title="Time on this question"
    >
      <svg
        className="ct-pill__ring"
        width={RING_SIZE}
        height={RING_SIZE}
        viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}
        aria-hidden="true"
      >
        <circle
          className="ct-pill__track"
          cx={c}
          cy={c}
          r={RING_R}
          fill="none"
          strokeWidth={RING_STROKE}
        />
        {progress > 0 && (
          <circle
            className="ct-pill__progress"
            cx={c}
            cy={c}
            r={RING_R}
            fill="none"
            strokeWidth={RING_STROKE}
            strokeLinecap="round"
            strokeDasharray={RING_CIRC}
            strokeDashoffset={RING_CIRC * (1 - progress)}
            transform={`rotate(-90 ${c} ${c})`}
          />
        )}
      </svg>
      <span className="ct-pill__time">{formatTime(elapsed)}</span>
    </div>
  )
}

export default CircularTimer
